import React, { useRef, useEffect } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Progress,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useSurvey } from "../../dashboard/hooks/useSurvey";
import { QuestionRenderer } from "./QuestionRenderers";

const lightTheme = {
  headingColor: "gray.700",
  textColor: "gray.500",
  textMuted: "gray.400",
  progressBg: "white",
  progressBorder: "gray.100",
  cardBg: "white",
  cardBorder: "gray.100",
  cardShadow: "sm",
  ghostColor: "gray.500",
  thankColor: "gwbi.700",
  thankText: "gray.600",
  unlockText: "gwbi.600",
};

const darkTheme = {
  headingColor: "white",
  textColor: "gray.200",
  textMuted: "gray.400",
  progressBg: "inherit",
  progressBorder: "whiteAlpha.300",
  cardBg: "whiteAlpha.200",
  cardBorder: "whiteAlpha.200",
  cardShadow: "none",
  ghostColor: "gray.300",
  thankColor: "white",
  thankText: "gray.200",
  unlockText: "gwbi.300",
};

export function SurveyRunner({
  code,
  sourceContext = "dashboard",
  locale,
  metadata,
  onComplete,
  onAbandon,
  variant = "light",
}) {
  const { t } = useTranslation("common");
  const theme = variant === "dark" ? darkTheme : lightTheme;
  const {
    survey,
    answers,
    errors,
    loading,
    saving,
    submitting,
    completed,
    unlockInfo,
    error,
    visibleQuestions,
    progressPct,
    setAnswer,
    submit,
  } = useSurvey(code, { sourceContext, locale, metadata });

  const firstErrorRef = useRef(null);

  useEffect(() => {
    if (completed && onComplete) onComplete(unlockInfo);
  }, [completed, unlockInfo, onComplete]);

  useEffect(() => {
    if (Object.keys(errors).length > 0 && firstErrorRef.current) {
      firstErrorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [errors]);

  if (loading) {
    return (
      <VStack py={12} spacing={4}>
        <Spinner size="lg" color="gwbi.500" />
        <Text color={theme.textColor}>{t("survey.loading", "Loading survey...")}</Text>
      </VStack>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error === "rate_limit"
          ? t("survey.rate_limit", "Too many attempts. Please try again later.")
          : error}
      </Alert>
    );
  }

  if (completed) {
    return (
      <VStack py={12} spacing={4} textAlign="center">
        <Heading size="md" color={theme.thankColor}>
          {t("survey.thank_you", "Thank you!")}
        </Heading>
        <Text color={theme.thankText}>
          {t("survey.completed_message", "Your responses have been recorded.")}
        </Text>
        {unlockInfo && (
          <Text fontSize="sm" color={theme.unlockText}>
            {t("survey.unlock_message", "You've unlocked {{feature}} until {{date}}.", {
              feature: unlockInfo.feature,
              date: new Date(unlockInfo.expires_at).toLocaleDateString(),
            })}
          </Text>
        )}
      </VStack>
    );
  }

  if (!survey) return null;

  const firstErrorCode = Object.keys(errors)[0];

  const header = (
    <Box
      flexShrink={0}
      bg={theme.progressBg}
      pb={3}
      pt={1}
      borderBottom="1px solid"
      borderColor={theme.progressBorder}
    >
      <HStack justify="space-between" mb={2}>
        <Heading size="sm" color={theme.headingColor}>{survey.title}</Heading>
        <HStack spacing={2}>
          {saving && <Text fontSize="xs" color={theme.textMuted}>{t("survey.saving", "Saving...")}</Text>}
          <Text fontSize="xs" color={theme.textColor}>{progressPct}%</Text>
        </HStack>
      </HStack>
      <Progress
        value={progressPct}
        size="xs"
        colorScheme="gwbi"
        borderRadius="full"
        aria-valuenow={progressPct}
        aria-valuemin={0}
        aria-valuemax={100}
      />
      {survey.description && (
        <Text fontSize="sm" color={theme.textColor} mt={2}>{survey.description}</Text>
      )}
    </Box>
  );

  if (variant === "dark") {
    return (
      <Flex direction="column" h="100%" minH={0}>
        {header}
        <Box flex="1" overflowY="auto" minH={0}>
          <VStack spacing={2} align="stretch" py={6}>
            {visibleQuestions.map((q) => (
              <Box
                key={q.code}
                ref={q.code === firstErrorCode ? firstErrorRef : undefined}
                bg={theme.cardBg}
                p={5}
                borderRadius="md"
                border="1px solid"
                borderColor={errors[q.code] ? "red.300" : theme.cardBorder}
                boxShadow={errors[q.code] ? "0 0 0 1px var(--chakra-colors-red-300)" : theme.cardShadow}
                transition="all 0.2s"
              >
                <QuestionRenderer
                  question={q}
                  value={answers[q.code]}
                  onChange={(val) => setAnswer(q.code, val)}
                  error={errors[q.code]}
                  variant={variant}
                />
              </Box>
            ))}
          </VStack>
        </Box>
        <HStack
          justify="space-between"
          pt={3}
          pb={3}
          flexShrink={0}
          borderTop="1px solid"
          borderColor="whiteAlpha.300"
        >
          <Button
            variant="ghost"
            color={theme.ghostColor}
            size="sm"
            onClick={() => {
              if (onAbandon) onAbandon();
            }}
          >
            {t("survey.save_later", "Save & continue later")}
          </Button>
          <Button
            bg="#5f2323"
            color="gray.200"
            _hover={{ bg: "#411717" }}
            size="md"
            onClick={submit}
            isLoading={submitting}
            isDisabled={submitting}
          >
            {t("survey.submit", "Submit")}
          </Button>
        </HStack>
      </Flex>
    );
  }

  return (
    <Box>
      {header}

      <VStack spacing={2} align="stretch" py={6}>
        {visibleQuestions.map((q) => (
          <Box
            key={q.code}
            ref={q.code === firstErrorCode ? firstErrorRef : undefined}
            bg={theme.cardBg}
            p={5}
            borderRadius="md"
            border="1px solid"
            borderColor={errors[q.code] ? "red.300" : theme.cardBorder}
            boxShadow={errors[q.code] ? "0 0 0 1px var(--chakra-colors-red-300)" : theme.cardShadow}
            transition="all 0.2s"
          >
            <QuestionRenderer
              question={q}
              value={answers[q.code]}
              onChange={(val) => setAnswer(q.code, val)}
              error={errors[q.code]}
              variant={variant}
            />
          </Box>
        ))}
      </VStack>

      <HStack justify="space-between" pt={4} pb={8}>
        <Button
          variant="ghost"
          color={theme.ghostColor}
          size="sm"
          onClick={() => {
            if (onAbandon) onAbandon();
          }}
        >
          {t("survey.save_later", "Save & continue later")}
        </Button>
        <Button
          colorScheme={variant === "dark" ? undefined : "gwbi"}
          bg={variant === "dark" ? "#5f2323" : undefined}
          color={variant === "dark" ? "gray.200" : undefined}
          _hover={variant === "dark" ? { bg: "#411717" } : undefined}
          size="md"
          onClick={submit}
          isLoading={submitting}
          isDisabled={submitting}
        >
          {t("survey.submit", "Submit")}
        </Button>
      </HStack>
    </Box>
  );
}
